import React, { useState, useMemo, useReducer, useEffect } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import {
  Button,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  FilterDirect,
  Icon
} from '@shopflo/ui'
import { MagnifyingGlass, Cube, CaretDown } from '@phosphor-icons/react'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { FilterOption } from 'src/components/Reports/ReportFilterDropdown'
import FilterDirectCheckBox from 'src/components/common/filter-direct-content/FilterDirectCheckBox'
import { debounce } from 'lodash'

import { selectionReducer, initialSelectionState, initializeSelectionState } from './lib/selectionReducer'
import { ROWS_PER_PAGE, searchParameterOptions } from './lib/productSelectorConstants'
import { useProductSearch } from './lib/useProductSearch'
import ProductRow from './ProductRow'
import { CatalogueDataType, ProductStatus } from 'src/lib/types/common/catalogue'
import { useLocale } from 'src/lib/hooks/useLocal'

export interface ProductSelectorContentProps {
  onApply: (selectedProducts: CatalogueDataType[]) => void
  onCancel: () => void
  catalogueType?: 'COLLECTION' | 'PRODUCT' | 'VARIANT' | 'PRODUCT_AND_VARIANT'
  initialSelectedProducts?: CatalogueDataType[]
  children?: React.ReactNode
  submitLabel?: string
  hideProductList?: boolean
  /** Product statuses to leave out of the list. See ProductSearchDialogProps. */
  excludedStatuses?: ProductStatus[]
  disabledVariants?: {
    ids: Set<string>
    reason: string
    label?: string
  }
  singleSelect?: boolean
  includeBundleProducts?: boolean
}

const ProductSelectorContent: React.FC<ProductSelectorContentProps> = ({
  onApply,
  onCancel,
  catalogueType = 'PRODUCT',
  initialSelectedProducts = [],
  children,
  submitLabel = 'Add',
  hideProductList = false,
  excludedStatuses,
  disabledVariants,
  singleSelect = false,
  includeBundleProducts = true
}) => {
  const {
    getters: { merchant }
  } = useMerchant()
  const { t } = useLocale()
  const finalSubmitLabel = submitLabel || t('product_search_dialog.add')

  const accountId = merchant?.accountId
  const issuerId = process.env.NEXT_PUBLIC_APP_ISSUER_ID ?? ''

  const [searchState, setSearchState] = useState({
    query: '',
    debouncedQuery: '',
    parameter: searchParameterOptions[0]
  })
  const [collapsedProducts, setCollapsedProducts] = useState<string[]>([])
  const [selectionState, dispatch] = useReducer(selectionReducer, initialSelectedProducts, products =>
    products && products.length > 0 ? initializeSelectionState(products) : initialSelectionState
  )
  const [sessionId] = useState(() => Date.now().toString())
  const { selectedProducts, selectedVariants } = selectionState

  const isProductSelected = (productId: string) => !!selectedProducts[productId]

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchState(prev => ({ ...prev, query: value }))
    debouncedSetSearch(value)
  }

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchState(prev => ({ ...prev, debouncedQuery: value }))
      }, 300),
    []
  )

  const { products: fetchedProducts, isLoadingMore, isReachingEnd, isEmpty, setSize } = useProductSearch({
    accountId,
    issuerId,
    catalogueType,
    searchQuery: searchState.debouncedQuery,
    searchParameter: searchState.parameter.value,
    sessionId,
    includeBundleProducts
  })

  /**
   * The catalogue endpoint takes no status filter, so excluded statuses are
   * dropped from the fetched pages instead.
   */
  const products = useMemo(() => {
    if (!excludedStatuses?.length) return fetchedProducts
    const excluded = new Set(excludedStatuses)
    return fetchedProducts.filter(product => !product.status || !excluded.has(product.status))
  }, [fetchedProducts, excludedStatuses])

  /**
   * Filtering happens after paging, so a page whose products are all excluded
   * adds no rows. The list then does not overflow, no scroll fires, and the
   * dialog looks empty even though more pages exist. Pull pages until there is
   * a page's worth to show or the catalogue runs out.
   */
  useEffect(() => {
    if (!excludedStatuses?.length || isLoadingMore || isReachingEnd || products.length >= ROWS_PER_PAGE) return
    setSize(size => size + 1)
  }, [excludedStatuses, products.length, isLoadingMore, isReachingEnd, setSize])

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget

    if (scrollHeight - scrollTop - clientHeight < 50) {
      if (!isLoadingMore && !isReachingEnd) {
        setSize(size => size + 1)
      }
    }
  }

  const toggleProductExpansion = (productId: string) => {
    setCollapsedProducts(prev =>
      prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId]
    )
  }

  const handleProductToggle = (product: CatalogueDataType) => {
    if (!product?.id) return
    dispatch({ type: 'TOGGLE_PRODUCT', product, singleSelect, disabledVariantIds: disabledVariants?.ids })
  }

  const handleVariantToggle = (product: CatalogueDataType, variant: CatalogueDataType) => {
    if (!product?.id || !variant?.id) return
    dispatch({ type: 'TOGGLE_VARIANT', product, variant, singleSelect })
  }

  const handleApply = () => {
    if (selectionState.isAllProductsSelected) {
      onApply([
        {
          id: catalogueType === 'COLLECTION' ? 'shopflo-all-collections' : 'shopflo-all-products',
          description: catalogueType === 'COLLECTION' ? 'All Collections' : 'All Products',
          imageUrl: '',
          variants: [],
          totalVariantCount: 0
        }
      ])
      return
    }
    const result = Object.values(selectedProducts).map(product => {
      const selectedVars = selectedVariants[product.id || '']
      if (selectedVars) {
        const fullProduct = products.find(p => p.id === product.id)
        const allVariants = fullProduct?.variants ?? product.variants ?? []
        return {
          ...product,
          variants: allVariants.filter(variant => selectedVars.includes(variant.id || ''))
        }
      }
      return product
    })
    onApply(result)
  }

  const isProductIndeterminate = (product: CatalogueDataType) => {
    if (!product?.variants?.length) return false
    const selectedVars = selectedVariants[product.id || '']
    if (!selectedVars) return false

    // Claimed variants can never be selected, so they do not count towards "all selected".
    const selectableCount = product.variants.filter(
      variant => !disabledVariants?.ids.has(variant.id as string)
    ).length

    return selectedVars.length > 0 && selectedVars.length < selectableCount
  }

  const getSelectedVariantIds = (productId: string) => {
    const variants = selectedVariants[productId]
    return variants ? new Set(variants) : undefined
  }

  const renderProductList = () => {
    if (products.length === 0 && isLoadingMore) {
      return (
        <div className='flex items-center justify-center py-12'>
          <div className='text-sm text-zinc-500'>{t('product_search_dialog.loading_products')}</div>
        </div>
      )
    }

    // Nothing to show either because the catalogue is empty or because every
    // loaded product was filtered out and there are no more pages.
    if (isEmpty || (products.length === 0 && isReachingEnd)) {
      return (
        <div className='flex items-center justify-center py-12'>
          <div className='text-sm text-zinc-500'>{t('product_search_dialog.no_products_found')}</div>
        </div>
      )
    }

    return (
      <div className='flex-1 overflow-y-auto' onScroll={handleScroll}>
        {products.flatMap(product => {
          if (!product?.id) return []
          const productId = product.id as string
          const isExpanded = !collapsedProducts.includes(productId)

          return (
            <ProductRow
              key={productId}
              product={product}
              isExpanded={isExpanded}
              isSelected={isProductSelected(productId)}
              isIndeterminate={isProductIndeterminate(product)}
              selectedVariantIds={getSelectedVariantIds(productId)}
              catalogueType={catalogueType}
              disabledVariants={disabledVariants}
              onToggleProduct={handleProductToggle}
              onToggleVariant={handleVariantToggle}
              onToggleExpand={toggleProductExpansion}
            />
          )
        })}

        {/* Loading More Indicator */}
        <div className='h-10 flex items-center justify-center shrink-0'>
          {isLoadingMore && products.length > 0 && (
            <div className='text-sm text-zinc-500'>{t('product_search_dialog.loading_more')}</div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className='flex flex-col h-full'>
      {/* Search & Filters */}
      <AnimatePresence initial={false}>
        {!hideProductList && (
          <motion.div
            initial={{ height: 'auto', opacity: 1 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className='overflow-hidden shrink-0'
          >
            <div className='px-5 pt-3 pb-0.5 flex flex-col gap-3'>
              <div className='relative'>
                <Input
                  variant='outline'
                  dimension='md'
                  placeholder={t(
                    catalogueType === 'COLLECTION'
                      ? 'product_search_dialog.search_collection_placeholder'
                      : 'product_search_dialog.search_product_placeholder'
                  )}
                  value={searchState.query}
                  onChange={handleSearchChange}
                  startAdornment={<MagnifyingGlass size={20} className='text-zinc-400' />}
                  endAdornment={
                    <Select
                      value={searchState.parameter.value}
                      onValueChange={value => {
                        const option = searchParameterOptions.find(opt => opt.value === value)
                        if (option) setSearchState(prev => ({ ...prev, parameter: option }))
                      }}
                    >
                      <SelectTrigger
                        showCaret={false}
                        className='w-[100px] !border-none !ring-0 !ring-offset-0 !text-zinc-600 !outline-none focus:!ring-0 focus:!ring-offset-0 focus:!outline-none  !justify-end'
                      >
                        <div className='flex items-center gap-1 justify-center'>
                          <SelectValue className='!px-1' />
                          <Icon IconComponent={CaretDown} size={14} className='text-zinc-500' />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {searchParameterOptions.map(option => (
                          <SelectItem key={option.value} value={option.value} className='!px-1'>
                            {option.value === 'name_like'
                              ? t('product_search_dialog.search_by_name')
                              : t('product_search_dialog.search_by_sku')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  }
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Table Header - Always Visible */}
      <div className='overflow-hidden px-5 shrink-0 pt-2.5 pb-3'>
        <div className='grid grid-cols-[3fr_1fr_1fr] gap-4 py-1.5 text-text-xs-medium font-medium text-zinc-500 border-b-1'>
          <div className='pl-8'>{t('product_search_dialog.title')}</div>
          <div className='text-right'>
            {catalogueType === 'COLLECTION' ? '' : t('product_search_dialog.quantity_in_stock')}
          </div>
          <div className='text-right pr-1'>{catalogueType === 'COLLECTION' ? '' : t('price')}</div>
        </div>
      </div>

      {/* List / Children */}
      {hideProductList ? (
        <div className='flex flex-col h-full px-5 min-h-0'>{children}</div>
      ) : (
        <div className='flex-1 overflow-hidden px-5 mb-3 flex flex-col'>
          {children}
          {renderProductList()}
        </div>
      )}

      {/* Footer */}
      <div className='pb-4 px-5 flex justify-end gap-2 shrink-0'>
        <Button variant='outline' size='md' onClick={onCancel}>
          {t('cancel')}
        </Button>
        <Button size='md' onClick={handleApply}>
          {finalSubmitLabel}
        </Button>
      </div>
    </div>
  )
}

export default ProductSelectorContent
